import React, { useState, useMemo, useEffect } from 'react';
import { 
  Plane, Building2, Bus, Star, Check, ChevronRight, 
  Sparkles, Crown, Wallet, Settings, Clock, MapPin, Users, UtensilsCrossed, Loader2, ArrowLeft,
  AlertTriangle, RefreshCw, X, CheckCircle2, XCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { useCurrency } from '../context/CurrencyContext';
import { searchHotels, searchFlights, getTransportOptions, verifyPackage } from '../services/umrahApi';

const PackageSelection = ({ 
  searchParams,
  onSelectPackage,
  onCustomize,
  onBack
}) => {
  const { formatPrice } = useCurrency();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [premiumBoardType, setPremiumBoardType] = useState('breakfast'); // breakfast, half_board, all_inclusive
  const [loading, setLoading] = useState(true);
  const [makkahHotels, setMakkahHotels] = useState([]);
  const [madinahHotels, setMadinahHotels] = useState([]);
  const [flightsData, setFlightsData] = useState({ outbound: [], inbound: [] });
  const [transportData, setTransportData] = useState(null); // Auto-selected transport based on passengers
  
  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [pendingPackage, setPendingPackage] = useState(null);
  
  const passengers = (searchParams?.adults || 2) + (searchParams?.children || 0) + (searchParams?.infants || 0);
  const makkahNights = searchParams?.makkahNights || 5;
  const madinahNights = searchParams?.madinahNights || 3;
  const totalNights = makkahNights + madinahNights;

  // Get recommended vehicle based on passenger count
  const getRecommendedVehicle = (transportOptions) => {
    if (!transportOptions || !transportOptions.length) return null;
    return transportOptions.find(v => v.is_recommended) || transportOptions[0];
  };

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch hotels, flights, and transport in parallel
        const [makkahData, madinahData, outboundFlightData, inboundFlightData, transportOptions] = await Promise.all([
          searchHotels({
            city: 'makkah',
            checkIn: searchParams.departureDate,
            checkOut: new Date(new Date(searchParams.departureDate).getTime() + makkahNights * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            guests: passengers,
            rooms: Math.ceil(passengers / 3)
          }),
          searchHotels({
            city: 'madinah',
            checkIn: new Date(new Date(searchParams.departureDate).getTime() + makkahNights * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            checkOut: searchParams.returnDate,
            guests: passengers,
            rooms: Math.ceil(passengers / 3)
          }),
          // Outbound flight (UK to arrival airport - JED or MED)
          searchFlights({
            origin: searchParams.departureAirport?.code || 'LHR',
            destination: searchParams.arrivalAirport || 'JED',
            departureDate: searchParams.departureDate,
            adults: searchParams.adults || 2,
            children: searchParams.children || 0,
            infants: searchParams.infants || 0
          }),
          // Return flight (from selected return city to UK)
          searchFlights({
            origin: searchParams.returnFrom || 'MED',
            destination: searchParams.departureAirport?.code || 'LHR',
            departureDate: searchParams.returnDate,
            adults: searchParams.adults || 2,
            children: searchParams.children || 0,
            infants: searchParams.infants || 0
          }),
          // Get transport options based on passenger count
          getTransportOptions(passengers)
        ]);
        
        setMakkahHotels(makkahData || []);
        setMadinahHotels(madinahData || []);
        setFlightsData({
          outbound: outboundFlightData || [],
          inbound: inboundFlightData || []
        });
        
        // Auto-select recommended transport based on passenger count
        const recommendedVehicle = getRecommendedVehicle(transportOptions);
        setTransportData(recommendedVehicle);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [searchParams, makkahNights, passengers]);

  // Helper function to calculate distance in km from string
  const parseDistance = (distanceStr) => {
    if (!distanceStr) return 999;
    const match = distanceStr.match(/(\d+\.?\d*)\s*(km|m)/i);
    if (match) {
      const value = parseFloat(match[1]);
      const unit = match[2].toLowerCase();
      return unit === 'm' ? value / 1000 : value;
    }
    if (distanceStr.toLowerCase().includes('direct') || distanceStr.toLowerCase().includes('haram access')) {
      return 0.1;
    }
    return 999;
  };

  // Filter and sort hotels by criteria
  const filterHotels = (hotels, criteria) => {
    return hotels.filter(hotel => {
      const distance = parseDistance(hotel.distance_to_haram);
      const stars = hotel.stars || 0;
      
      if (criteria.maxDistance && distance > criteria.maxDistance) return false;
      if (criteria.minStars && stars < criteria.minStars) return false;
      if (criteria.requireBreakfast && !hotel.breakfast_included) return false;
      
      return true;
    }).sort((a, b) => {
      if (criteria.sortBy === 'price') {
        return (a.price_per_night || 0) - (b.price_per_night || 0);
      }
      if (criteria.sortBy === 'distance') {
        return parseDistance(a.distance_to_haram) - parseDistance(b.distance_to_haram);
      }
      return 0;
    });
  };

  // Filter flights by criteria
  const filterFlights = (flights, criteria) => {
    if (!flights || !flights.length) return [];
    
    return flights.filter(flight => {
      const stops = flight.legs?.[0]?.stops || 0;
      
      if (criteria.directOnly && stops > 0) return false;
      if (criteria.maxStops !== undefined && stops > criteria.maxStops) return false;
      
      // Check layover duration for premium
      if (criteria.maxLayover && stops > 0) {
        const segments = flight.legs?.[0]?.segments || [];
        for (let i = 0; i < segments.length - 1; i++) {
          const arrTime = new Date(segments[i].arrival_time);
          const depTime = new Date(segments[i + 1].departure_time);
          const layoverMinutes = (depTime - arrTime) / (1000 * 60);
          if (layoverMinutes > criteria.maxLayover) return false;
        }
      }
      
      return true;
    }).sort((a, b) => {
      return (a.pricing?.total_fare || 0) - (b.pricing?.total_fare || 0);
    });
  };

  // Find combinable fares (same airline for outbound and inbound)
  const findCombinableFares = (outboundFlights, inboundFlights, criteria = {}) => {
    if (!outboundFlights?.length || !inboundFlights?.length) return { combinable: [], alternatives: [] };
    
    const filteredOutbound = criteria.outbound ? filterFlights(outboundFlights, criteria.outbound) : outboundFlights;
    const filteredInbound = criteria.inbound ? filterFlights(inboundFlights, criteria.inbound) : inboundFlights;
    
    const combinable = [];
    const alternatives = [];
    
    // Group by airline for combinable fares
    const airlineMap = {};
    
    filteredOutbound.forEach(outbound => {
      const outboundAirline = outbound.validating_carrier || outbound.legs?.[0]?.segments?.[0]?.airline;
      if (!airlineMap[outboundAirline]) {
        airlineMap[outboundAirline] = { outbound: [], inbound: [] };
      }
      airlineMap[outboundAirline].outbound.push(outbound);
    });
    
    filteredInbound.forEach(inbound => {
      const inboundAirline = inbound.validating_carrier || inbound.legs?.[0]?.segments?.[0]?.airline;
      if (airlineMap[inboundAirline]) {
        airlineMap[inboundAirline].inbound.push(inbound);
      }
    });
    
    // Create combinable fare pairs (same airline)
    Object.keys(airlineMap).forEach(airline => {
      const { outbound, inbound } = airlineMap[airline];
      if (outbound.length > 0 && inbound.length > 0) {
        // Take the cheapest of each for this airline
        const cheapestOutbound = outbound.sort((a, b) => (a.pricing?.total_fare || 0) - (b.pricing?.total_fare || 0))[0];
        const cheapestInbound = inbound.sort((a, b) => (a.pricing?.total_fare || 0) - (b.pricing?.total_fare || 0))[0];
        
        const combinedPrice = (cheapestOutbound.pricing?.total_fare || 0) + (cheapestInbound.pricing?.total_fare || 0);
        // Apply 5% discount for combinable fares (same airline)
        const discountedPrice = Math.round(combinedPrice * 0.95);
        
        combinable.push({
          outbound: cheapestOutbound,
          inbound: cheapestInbound,
          airline: airline,
          airlineName: cheapestOutbound.legs?.[0]?.segments?.[0]?.airline_name || airline,
          originalPrice: combinedPrice,
          combinedPrice: discountedPrice,
          savings: combinedPrice - discountedPrice,
          isCombinable: true,
          type: 'combinable'
        });
      }
    });
    
    // Sort combinable by price
    combinable.sort((a, b) => a.combinedPrice - b.combinedPrice);
    
    // Create alternative pairs (different airlines - cheapest mix)
    if (filteredOutbound.length > 0 && filteredInbound.length > 0) {
      const cheapestOutbound = filteredOutbound.sort((a, b) => (a.pricing?.total_fare || 0) - (b.pricing?.total_fare || 0))[0];
      const cheapestInbound = filteredInbound.sort((a, b) => (a.pricing?.total_fare || 0) - (b.pricing?.total_fare || 0))[0];
      
      const outboundAirline = cheapestOutbound.validating_carrier || cheapestOutbound.legs?.[0]?.segments?.[0]?.airline;
      const inboundAirline = cheapestInbound.validating_carrier || cheapestInbound.legs?.[0]?.segments?.[0]?.airline;
      
      // Only add as alternative if it's actually different airlines
      if (outboundAirline !== inboundAirline) {
        const combinedPrice = (cheapestOutbound.pricing?.total_fare || 0) + (cheapestInbound.pricing?.total_fare || 0);
        
        alternatives.push({
          outbound: cheapestOutbound,
          inbound: cheapestInbound,
          outboundAirline: outboundAirline,
          inboundAirline: inboundAirline,
          outboundAirlineName: cheapestOutbound.legs?.[0]?.segments?.[0]?.airline_name || outboundAirline,
          inboundAirlineName: cheapestInbound.legs?.[0]?.segments?.[0]?.airline_name || inboundAirline,
          combinedPrice: combinedPrice,
          isCombinable: false,
          type: 'alternative'
        });
      }
    }
    
    return { combinable, alternatives };
  };

  // Get best flight combination (prioritize combinable, then alternatives)
  const getBestFlightCombination = (criteria = {}) => {
    const { combinable, alternatives } = findCombinableFares(
      flightsData.outbound, 
      flightsData.inbound,
      criteria
    );
    
    // Return cheapest combinable if available
    if (combinable.length > 0) {
      return combinable[0];
    }
    
    // Otherwise return cheapest alternative
    if (alternatives.length > 0) {
      return alternatives[0];
    }
    
    // Fallback to first available flights
    if (flightsData.outbound?.length && flightsData.inbound?.length) {
      return {
        outbound: flightsData.outbound[0],
        inbound: flightsData.inbound[0],
        combinedPrice: (flightsData.outbound[0].pricing?.total_fare || 0) + (flightsData.inbound[0].pricing?.total_fare || 0),
        isCombinable: false,
        type: 'fallback'
      };
    }
    
    return null;
  };

  // Generate Economy Package
  const economyPackage = useMemo(() => {
    // Cheapest hotels within 2.5km
    const economyMakkah = filterHotels(makkahHotels, { 
      maxDistance: 2.5, 
      sortBy: 'price' 
    });
    const economyMadinah = filterHotels(madinahHotels, { 
      maxDistance: 2.5, 
      sortBy: 'price' 
    });
    
    // Get best flight combination (prioritize combinable fares)
    const flightCombo = getBestFlightCombination({});
    
    const makkahHotel = economyMakkah[0];
    const madinahHotel = economyMadinah[0];
    
    if (!makkahHotel || !madinahHotel || !flightCombo) return null;
    
    // Package price = Hotels + Flights + Visa (Transport is separate)
    const hotelCost = (makkahHotel.price_per_night * makkahNights + madinahHotel.price_per_night * madinahNights) * passengers;
    const flightCost = flightCombo.combinedPrice || ((flightCombo.outbound?.pricing?.total_fare || 500) + (flightCombo.inbound?.pricing?.total_fare || 400));
    const visaCost = 50 * passengers;
    const packageTotal = hotelCost + flightCost + visaCost;
    
    // Transport is calculated separately based on passenger count
    const transportCost = transportData?.standard_package_price || 0;
    const grandTotal = packageTotal + transportCost;
    
    return {
      type: 'economy',
      name: 'Economy Package',
      tagline: 'Best Value for Money',
      icon: Wallet,
      color: 'from-emerald-500 to-green-600',
      borderColor: 'border-emerald-500',
      bgColor: 'bg-emerald-500/10',
      makkahHotel,
      madinahHotel,
      outboundFlight: flightCombo.outbound,
      inboundFlight: flightCombo.inbound,
      flightCombo: flightCombo,
      boardType: 'room_only',
      pricing: {
        hotels: hotelCost,
        flights: flightCost,
        flightSavings: flightCombo.savings || 0,
        visa: visaCost,
        packageTotal,
        transport: transportCost,
        total: grandTotal,
        perPerson: Math.round(grandTotal / passengers)
      },
      features: [
        { icon: Building2, text: `Hotels within 2.5km of Haram` },
        { icon: Plane, text: flightCombo.isCombinable ? 'Combinable fare (same airline)' : 'Best value flights' },
        { icon: Star, text: 'Room only (no meals)' }
      ],
      availability: {
        available: true,
        message: null
      }
    };
  }, [makkahHotels, madinahHotels, flightsData, makkahNights, madinahNights, passengers, transportData]);

  // Generate Standard Package
  const standardPackage = useMemo(() => {
    // 4-star+ hotels within 1 mile (1.6km) with breakfast
    const standardMakkah = filterHotels(makkahHotels, { 
      maxDistance: 1.6, 
      minStars: 4,
      sortBy: 'price' 
    });
    const standardMadinah = filterHotels(madinahHotels, { 
      maxDistance: 1.6, 
      minStars: 4,
      sortBy: 'price' 
    });
    
    // Get best flight combination with preference for direct/short-stop
    const flightCombo = getBestFlightCombination({
      outbound: { maxStops: 1 },
      inbound: { maxStops: 1 }
    });
    
    let makkahHotel = standardMakkah[0] || filterHotels(makkahHotels, { minStars: 4, sortBy: 'price' })[0];
    let madinahHotel = standardMadinah[0] || filterHotels(madinahHotels, { minStars: 4, sortBy: 'price' })[0];
    
    // Check availability status
    let availability = { available: true, message: null };
    
    if (!makkahHotel || !madinahHotel) {
      // Fallback to any available hotels
      makkahHotel = makkahHotel || makkahHotels[0];
      madinahHotel = madinahHotel || madinahHotels[0];
      availability = {
        available: makkahHotel && madinahHotel,
        message: makkahHotel && madinahHotel 
          ? 'Limited 4-star availability - showing best alternative'
          : 'Not available for selected dates'
      };
    }
    
    if (!makkahHotel || !madinahHotel) {
      // Return placeholder package
      return {
        type: 'standard',
        name: 'Standard Package',
        tagline: 'Quality & Comfort',
        icon: Star,
        color: 'from-blue-500 to-indigo-600',
        borderColor: 'border-blue-500',
        bgColor: 'bg-blue-500/10',
        makkahHotel: null,
        madinahHotel: null,
        availability: {
          available: false,
          message: 'Not available for selected dates'
        },
        pricing: { total: 0, perPerson: 0 }
      };
    }
    
    // Add breakfast cost if not included
    const breakfastCost = (!makkahHotel.breakfast_included ? 15 * makkahNights : 0) + 
                          (!madinahHotel.breakfast_included ? 15 * madinahNights : 0);
    const hotelCost = ((makkahHotel.price_per_night * makkahNights + madinahHotel.price_per_night * madinahNights) * passengers) + (breakfastCost * passengers);
    const flightCost = flightCombo?.combinedPrice || ((flightCombo?.outbound?.pricing?.total_fare || 600) + (flightCombo?.inbound?.pricing?.total_fare || 500));
    const visaCost = 50 * passengers;
    const packageTotal = hotelCost + flightCost + visaCost;
    
    // Transport is calculated separately based on passenger count
    const transportCost = transportData?.standard_package_price || 0;
    const grandTotal = packageTotal + transportCost;
    
    return {
      type: 'standard',
      name: 'Standard Package',
      tagline: 'Quality & Comfort',
      icon: Star,
      color: 'from-blue-500 to-indigo-600',
      borderColor: 'border-blue-500',
      bgColor: 'bg-blue-500/10',
      makkahHotel,
      madinahHotel,
      outboundFlight: flightCombo?.outbound,
      inboundFlight: flightCombo?.inbound,
      flightCombo: flightCombo,
      boardType: 'breakfast',
      pricing: {
        hotels: hotelCost,
        flights: flightCost,
        flightSavings: flightCombo?.savings || 0,
        visa: visaCost,
        packageTotal,
        transport: transportCost,
        total: grandTotal,
        perPerson: Math.round(grandTotal / passengers)
      },
      features: [
        { icon: Building2, text: `4-Star hotels within 1 mile of Haram` },
        { icon: UtensilsCrossed, text: 'Breakfast included' },
        { icon: Plane, text: flightCombo?.isCombinable ? 'Combinable fare (direct/1-stop)' : 'Direct or short-stop flights' }
      ],
      availability
    };
  }, [makkahHotels, madinahHotels, flightsData, makkahNights, madinahNights, passengers, transportData]);

  // Generate Premium Package
  const premiumPackage = useMemo(() => {
    // 5-star hotels within 0.4 miles (0.64km)
    const premiumMakkah = filterHotels(makkahHotels, { 
      maxDistance: 0.64, 
      minStars: 5,
      sortBy: 'distance' 
    });
    const premiumMadinah = filterHotels(madinahHotels, { 
      maxDistance: 0.64, 
      minStars: 5,
      sortBy: 'distance' 
    });
    
    // Get best flight combination with preference for direct flights
    const isJeddah = searchParams?.arrivalAirport === 'JED';
    const flightCombo = getBestFlightCombination({
      outbound: isJeddah ? { directOnly: true } : { maxStops: 1, maxLayover: 120 },
      inbound: { directOnly: true }
    });
    
    // Fallback to any direct combo if strict criteria returns nothing
    const fallbackCombo = !flightCombo ? getBestFlightCombination({
      outbound: { directOnly: true },
      inbound: { directOnly: true }
    }) : null;
    
    const finalFlightCombo = flightCombo || fallbackCombo || getBestFlightCombination({});
    
    let makkahHotel = premiumMakkah[0] || filterHotels(makkahHotels, { minStars: 5, sortBy: 'price' })[0];
    let madinahHotel = premiumMadinah[0] || filterHotels(madinahHotels, { minStars: 5, sortBy: 'price' })[0];
    
    // Check availability status
    let availability = { available: true, message: null };
    
    if (!makkahHotel || !madinahHotel) {
      // Fallback to best available
      makkahHotel = makkahHotel || filterHotels(makkahHotels, { minStars: 4, sortBy: 'price' })[0] || makkahHotels[0];
      madinahHotel = madinahHotel || filterHotels(madinahHotels, { minStars: 4, sortBy: 'price' })[0] || madinahHotels[0];
      availability = {
        available: makkahHotel && madinahHotel,
        message: makkahHotel && madinahHotel
          ? 'Limited 5-star availability - showing best alternative'
          : 'Not available for selected dates'
      };
    }
    
    if (!makkahHotel || !madinahHotel) {
      // Return placeholder package
      return {
        type: 'premium',
        name: 'Premium Package',
        tagline: 'Luxury Experience',
        icon: Crown,
        color: 'from-amber-500 to-yellow-600',
        borderColor: 'border-amber-500',
        bgColor: 'bg-amber-500/10',
        recommended: true,
        makkahHotel: null,
        madinahHotel: null,
        availability: {
          available: false,
          message: 'Not available for selected dates'
        },
        pricing: { total: 0, perPerson: 0 }
      };
    }
    
    // Board type pricing
    const boardPricing = {
      breakfast: 0,
      half_board: 25 * totalNights * passengers,
      all_inclusive: 50 * totalNights * passengers
    };
    
    const hotelCost = (makkahHotel.price_per_night * makkahNights + madinahHotel.price_per_night * madinahNights) * passengers;
    const flightCost = finalFlightCombo?.combinedPrice || ((finalFlightCombo?.outbound?.pricing?.total_fare || 800) + (finalFlightCombo?.inbound?.pricing?.total_fare || 700));
    const visaCost = 50 * passengers;
    const mealsCost = boardPricing[premiumBoardType];
    const packageTotal = hotelCost + flightCost + visaCost + mealsCost;
    
    // Transport is calculated separately based on passenger count
    const transportCost = transportData?.standard_package_price || 0;
    const grandTotal = packageTotal + transportCost;
    
    return {
      type: 'premium',
      name: 'Premium Package',
      tagline: 'Luxury Experience',
      icon: Crown,
      color: 'from-amber-500 to-yellow-600',
      borderColor: 'border-amber-500',
      bgColor: 'bg-amber-500/10',
      recommended: true,
      makkahHotel,
      madinahHotel,
      outboundFlight: finalFlightCombo?.outbound,
      inboundFlight: finalFlightCombo?.inbound,
      flightCombo: finalFlightCombo,
      boardType: premiumBoardType,
      boardOptions: [
        { id: 'breakfast', name: 'Breakfast Only', price: 0 },
        { id: 'half_board', name: 'Breakfast + Dinner', price: boardPricing.half_board },
        { id: 'all_inclusive', name: 'All Inclusive', price: boardPricing.all_inclusive }
      ],
      pricing: {
        hotels: hotelCost,
        flights: flightCost,
        flightSavings: finalFlightCombo?.savings || 0,
        visa: visaCost,
        meals: mealsCost,
        packageTotal,
        transport: transportCost,
        total: grandTotal,
        perPerson: Math.round(grandTotal / passengers)
      },
      features: [
        { icon: Building2, text: `5-Star hotels walking distance to Haram` },
        { icon: UtensilsCrossed, text: 'Flexible meal options' },
        { icon: Plane, text: finalFlightCombo.isCombinable ? 'Combinable direct fare' : 'Direct flights (Jeddah guaranteed)' },
        { icon: Sparkles, text: 'Priority check-in & support' }
      ],
      availability
    };
  }, [makkahHotels, madinahHotels, flightsData, makkahNights, madinahNights, passengers, premiumBoardType, searchParams, totalNights, transportData]);

  // Always include all three packages, even if some are unavailable
  const packages = [economyPackage, standardPackage, premiumPackage].filter(pkg => pkg !== null);

  // Verify package before proceeding
  const verifyAndSelectPackage = async (pkg) => {
    // Don't allow selection of unavailable packages
    if (pkg.availability && !pkg.availability.available) {
      return;
    }
    
    setPendingPackage(pkg);
    setVerifying(true);
    setShowVerificationModal(true);
    setVerificationResult(null);
    
    try {
      const result = await verifyPackage({
        packageType: pkg.type,
        makkahHotelId: pkg.makkahHotel?.id || '',
        madinahHotelId: pkg.madinahHotel?.id || '',
        makkahNights: makkahNights,
        madinahNights: madinahNights,
        departureAirport: searchParams.departureAirport?.code || 'LHR',
        arrivalAirport: searchParams.arrivalAirport || 'JED',
        returnFrom: searchParams.returnFrom || 'MED',
        departureDate: searchParams.departureDate,
        returnDate: searchParams.returnDate,
        adults: searchParams.adults || 2,
        children: searchParams.children || 0,
        infants: searchParams.infants || 0,
        boardType: pkg.boardType || 'room_only',
        originalPrice: pkg.pricing?.total || 0,
        currency: 'GBP'
      });
      
      setVerificationResult(result);
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationResult({
        verified: false,
        issues: ['Unable to verify package. Please try again.'],
        warnings: []
      });
    } finally {
      setVerifying(false);
    }
  };

  // Proceed with package after verification
  const proceedWithPackage = () => {
    if (!pendingPackage) return;
    
    const updatedPricing = verificationResult?.price_changed 
      ? { ...pendingPackage.pricing, total: verificationResult.new_price, perPerson: Math.round(verificationResult.new_price / passengers) }
      : pendingPackage.pricing;
    
    setSelectedPackage(pendingPackage.type);
    setShowVerificationModal(false);
    
    onSelectPackage && onSelectPackage({
      packageType: pendingPackage.type,
      makkahHotel: verificationResult?.makkah_hotel || pendingPackage.makkahHotel,
      madinahHotel: verificationResult?.madinah_hotel || pendingPackage.madinahHotel,
      outboundFlight: verificationResult?.outbound_flight || pendingPackage.outboundFlight,
      inboundFlight: verificationResult?.return_flight || pendingPackage.inboundFlight,
      boardType: pendingPackage.boardType,
      pricing: updatedPricing,
      transport: transportData,
      verified: true
    });
  };

  const handleSelectPackage = (pkg) => {
    verifyAndSelectPackage(pkg);
  };

  // Close verification modal
  const closeVerificationModal = () => {
    setShowVerificationModal(false);
    setPendingPackage(null);
    setVerificationResult(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#d4af37] animate-spin mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Finding Best Packages...</h2>
          <p className="text-gray-400">Searching hotels and flights for your Umrah journey</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[#d4af37] hover:text-[#e5c158] mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Search
        </button>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            Choose Your Umrah Package
          </h1>
          <p className="text-gray-400">
            {passengers} Travellers • {makkahNights} nights Makkah • {madinahNights} nights Madinah
          </p>
        </div>

        {/* Package Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {packages.map((pkg) => {
            const Icon = pkg.icon;
            const isSelected = selectedPackage === pkg.type;
            
            return (
              <div 
                key={pkg.type}
                className={`relative bg-[#1a2d4a] rounded-2xl overflow-hidden border-2 transition-all cursor-pointer hover:scale-[1.02] ${
                  isSelected ? pkg.borderColor + ' shadow-lg' : 'border-transparent hover:border-gray-600'
                }`}
                onClick={() => setSelectedPackage(pkg.type)}
              >
                {/* Recommended Badge */}
                {pkg.recommended && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-amber-500 to-yellow-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                    RECOMMENDED
                  </div>
                )}
                
                {/* Header */}
                <div className={`bg-gradient-to-r ${pkg.color} p-4`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{pkg.name}</h3>
                      <p className="text-white/80 text-sm">{pkg.tagline}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-4">
                  {/* Combinable Fare Badge */}
                  {pkg.flightCombo?.isCombinable && (
                    <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <span className="text-green-400 font-semibold text-sm">Combinable Fare</span>
                        <span className="text-gray-400 text-xs ml-2">Same airline • Best price</span>
                      </div>
                      {pkg.pricing?.flightSavings > 0 && (
                        <span className="ml-auto text-green-400 text-xs font-medium">
                          Save {formatPrice(pkg.pricing.flightSavings)}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Alternative Fare Notice */}
                  {pkg.flightCombo && !pkg.flightCombo.isCombinable && pkg.flightCombo.type === 'alternative' && (
                    <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Plane className="w-3 h-3 text-white" />
                      </div>
                      <div>
                        <span className="text-blue-400 font-semibold text-sm">Best Mix & Match</span>
                        <span className="text-gray-400 text-xs ml-2">Different airlines • Cheapest combo</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Outbound Flight Details */}
                  <div className="bg-[#0a1628] rounded-xl p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                      <Plane className="w-3 h-3" />
                      <span>Outbound Flight</span>
                    </div>
                    {pkg.outboundFlight ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-lg font-bold text-gray-800">
                          {pkg.outboundFlight.validating_carrier || pkg.outboundFlight.legs?.[0]?.segments?.[0]?.airline || 'FL'}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-medium text-sm">
                            {pkg.outboundFlight.legs?.[0]?.segments?.[0]?.airline_name || 'Airline'}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                            <span>{searchParams.departureAirport?.code || 'LON'}</span>
                            <span>→</span>
                            <span>{searchParams.arrivalAirport || 'JED'}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${pkg.outboundFlight.legs?.[0]?.stops === 0 ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {pkg.outboundFlight.legs?.[0]?.stops === 0 
                                ? 'Direct' 
                                : pkg.outboundFlight.legs?.[0]?.layover 
                                  ? `${pkg.outboundFlight.legs[0].layover.duration} in ${pkg.outboundFlight.legs[0].layover.city}`
                                  : `${pkg.outboundFlight.legs?.[0]?.stops || 1} Stop`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span>{searchParams.departureDate ? new Date(searchParams.departureDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                            <span>•</span>
                            <span>{pkg.outboundFlight.legs?.[0]?.duration || '6h 30m'}</span>
                            <span>•</span>
                            <span>{pkg.outboundFlight.baggage?.checked_bags || 2}x{pkg.outboundFlight.baggage?.weight_per_bag || '23kg'}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">Flight TBD</div>
                    )}
                  </div>

                  {/* Return Flight Details */}
                  <div className="bg-[#0a1628] rounded-xl p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                      <Plane className="w-3 h-3 rotate-180" />
                      <span>Return Flight</span>
                    </div>
                    {pkg.inboundFlight ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-lg font-bold text-gray-800">
                          {pkg.inboundFlight.validating_carrier || pkg.inboundFlight.legs?.[0]?.segments?.[0]?.airline || 'FL'}
                        </div>
                        <div className="flex-1">
                          <div className="text-white font-medium text-sm">
                            {pkg.inboundFlight.legs?.[0]?.segments?.[0]?.airline_name || 'Airline'}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                            <span>{searchParams.returnFrom || 'MED'}</span>
                            <span>→</span>
                            <span>{searchParams.departureAirport?.code || 'LON'}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${pkg.inboundFlight.legs?.[0]?.stops === 0 ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                              {pkg.inboundFlight.legs?.[0]?.stops === 0 
                                ? 'Direct' 
                                : pkg.inboundFlight.legs?.[0]?.layover 
                                  ? `${pkg.inboundFlight.legs[0].layover.duration} in ${pkg.inboundFlight.legs[0].layover.city}`
                                  : `${pkg.inboundFlight.legs?.[0]?.stops || 1} Stop`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span>{searchParams.returnDate ? new Date(searchParams.returnDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</span>
                            <span>•</span>
                            <span>{pkg.inboundFlight.legs?.[0]?.duration || '6h 30m'}</span>
                            <span>•</span>
                            <span>{pkg.inboundFlight.baggage?.checked_bags || 2}x{pkg.inboundFlight.baggage?.weight_per_bag || '23kg'}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 text-sm">Flight TBD</div>
                    )}
                  </div>

                  {/* Makkah Hotel */}
                  <div className="bg-[#0a1628] rounded-xl p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                      <Building2 className="w-3 h-3" />
                      <span>Makkah Hotel • {makkahNights} nights</span>
                    </div>
                    <div className="text-white font-medium text-sm">{pkg.makkahHotel?.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[#d4af37] text-xs">{'★'.repeat(pkg.makkahHotel?.stars || 0)}</span>
                      <span className="text-gray-500 text-xs">•</span>
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pkg.makkahHotel?.distance_to_haram || '0.5km'} to Haram
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {pkg.boardType === 'room_only' ? 'Room Only' : 
                       pkg.boardType === 'breakfast' ? 'Breakfast Included' :
                       pkg.boardType === 'half_board' ? 'Half Board' : 'All Inclusive'}
                    </div>
                  </div>

                  {/* Madinah Hotel */}
                  <div className="bg-[#0a1628] rounded-xl p-3">
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-2">
                      <Building2 className="w-3 h-3" />
                      <span>Madinah Hotel • {madinahNights} nights</span>
                    </div>
                    <div className="text-white font-medium text-sm">{pkg.madinahHotel?.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[#d4af37] text-xs">{'★'.repeat(pkg.madinahHotel?.stars || 0)}</span>
                      <span className="text-gray-500 text-xs">•</span>
                      <span className="text-gray-400 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {pkg.madinahHotel?.distance_to_haram || '0.5km'} to Haram
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {pkg.boardType === 'room_only' ? 'Room Only' : 
                       pkg.boardType === 'breakfast' ? 'Breakfast Included' :
                       pkg.boardType === 'half_board' ? 'Half Board' : 'All Inclusive'}
                    </div>
                  </div>

                  {/* Premium Board Options */}
                  {pkg.type === 'premium' && pkg.boardOptions && (
                    <div className="pt-2 border-t border-gray-700">
                      <p className="text-xs text-gray-400 mb-2">Meal Plan:</p>
                      <div className="space-y-1">
                        {pkg.boardOptions.map((option) => (
                          <label 
                            key={option.id}
                            className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                              premiumBoardType === option.id 
                                ? 'bg-amber-500/20 border border-amber-500/50' 
                                : 'bg-[#0a1628] hover:bg-[#0a1628]/80'
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPremiumBoardType(option.id);
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <input 
                                type="radio"
                                name="boardType"
                                checked={premiumBoardType === option.id}
                                onChange={() => setPremiumBoardType(option.id)}
                                className="text-amber-500"
                              />
                              <span className="text-white text-xs">{option.name}</span>
                            </div>
                            {option.price > 0 && (
                              <span className="text-amber-400 text-xs">+{formatPrice(option.price)}</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price */}
                  <div className="pt-3 border-t border-gray-700">
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-gray-400 text-sm">From</span>
                        <div className="text-2xl font-bold text-[#d4af37]">
                          {formatPrice(pkg.pricing.perPerson)}
                        </div>
                        <span className="text-gray-400 text-xs">per person</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-400 text-xs">Total</span>
                        <div className="text-white font-semibold">
                          {formatPrice(pkg.pricing.total)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Select Button */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPackage(pkg);
                    }}
                    className={`w-full py-3 font-bold rounded-xl transition-all ${
                      isSelected 
                        ? 'bg-[#d4af37] text-[#0a1628] hover:bg-[#e5c158]' 
                        : 'bg-[#0a1628] text-white border border-[#d4af37]/50 hover:bg-[#d4af37]/20'
                    }`}
                  >
                    {isSelected ? (
                      <span className="flex items-center justify-center gap-2">
                        <Check className="w-5 h-5" /> Selected
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        Select Package <ChevronRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}

          {/* Customise Package Card */}
          <div 
            className="relative bg-[#1a2d4a] rounded-2xl overflow-hidden border-2 border-dashed border-gray-600 hover:border-[#d4af37] transition-all cursor-pointer hover:scale-[1.02]"
            onClick={onCustomize}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Custom Package</h3>
                  <p className="text-white/80 text-sm">Build Your Own</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <div className="text-center py-8">
                <Settings className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                <h4 className="text-white font-semibold mb-2">Fully Customisable</h4>
                <p className="text-gray-400 text-sm">
                  Select your own hotels, flights, transport, and services
                </p>
              </div>

              <div className="space-y-1.5 pt-2 border-t border-gray-700">
                <div className="flex items-center gap-2 text-gray-300 text-xs">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Choose any hotel</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-xs">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Select preferred flights</span>
                </div>
                <div className="flex items-center gap-2 text-gray-300 text-xs">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>Add optional services</span>
                </div>
              </div>

              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onCustomize && onCustomize();
                }}
                className="w-full py-3 font-bold rounded-xl bg-[#0a1628] text-white border border-[#d4af37]/50 hover:bg-[#d4af37]/20 transition-all"
              >
                <span className="flex items-center justify-center gap-2">
                  Customise <ChevronRight className="w-5 h-5" />
                </span>
              </Button>
            </div>
          </div>
        </div>

        {/* Transport Summary - Auto-selected based on passenger count */}
        {transportData && (
          <div className="mt-8 bg-[#1a2d4a] rounded-2xl p-6 border border-[#d4af37]/20">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Bus className="w-5 h-5 text-[#d4af37]" />
                  <h3 className="text-lg font-bold text-white">Transport Included</h3>
                  <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">
                    Auto-selected for {passengers} passengers
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  <span className="text-white font-medium">{transportData.name}</span> • {transportData.description}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  Includes: Jeddah Airport → Makkah • Makkah → Madinah • Madinah → Jeddah Airport
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#d4af37]">
                  {formatPrice(transportData.standard_package_price || 0)}
                </div>
                <span className="text-gray-400 text-xs">per vehicle</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a2d4a] rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl border border-[#d4af37]/20">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#d4af37] to-[#b8962e] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    {verifying ? (
                      <RefreshCw className="w-5 h-5 text-white animate-spin" />
                    ) : verificationResult?.verified ? (
                      <CheckCircle2 className="w-5 h-5 text-white" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {verifying ? 'Verifying Package...' : 
                       verificationResult?.verified ? 'Package Verified' : 'Verification Issue'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {verifying ? 'Checking availability & pricing' : 
                       verificationResult?.verified ? 'Ready to proceed' : 'Please review'}
                    </p>
                  </div>
                </div>
                {!verifying && (
                  <button 
                    onClick={closeVerificationModal}
                    className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {verifying ? (
                <div className="text-center py-8">
                  <div className="relative w-20 h-20 mx-auto mb-4">
                    <div className="absolute inset-0 border-4 border-[#d4af37]/20 rounded-full"></div>
                    <div className="absolute inset-0 border-4 border-[#d4af37] rounded-full border-t-transparent animate-spin"></div>
                    <div className="absolute inset-2 bg-[#0a1628] rounded-full flex items-center justify-center">
                      <RefreshCw className="w-6 h-6 text-[#d4af37]" />
                    </div>
                  </div>
                  <h4 className="text-white font-semibold mb-2">Final Research in Progress</h4>
                  <p className="text-gray-400 text-sm mb-4">
                    Re-checking flights and hotels to ensure everything is available...
                  </p>
                  <div className="space-y-2 text-left max-w-xs mx-auto">
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 text-[#d4af37] animate-spin" />
                      <span className="text-gray-300">Verifying hotel availability...</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 text-[#d4af37] animate-spin" />
                      <span className="text-gray-300">Checking flight prices...</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Loader2 className="w-4 h-4 text-[#d4af37] animate-spin" />
                      <span className="text-gray-300">Confirming package pricing...</span>
                    </div>
                  </div>
                </div>
              ) : verificationResult ? (
                <div className="space-y-4">
                  {/* Verification Status */}
                  <div className={`p-4 rounded-xl ${
                    verificationResult.verified 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <div className="flex items-center gap-3">
                      {verificationResult.verified ? (
                        <CheckCircle2 className="w-8 h-8 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-500 flex-shrink-0" />
                      )}
                      <div>
                        <h4 className={`font-semibold ${verificationResult.verified ? 'text-green-400' : 'text-red-400'}`}>
                          {verificationResult.verified ? 'All Items Available' : 'Some Items Unavailable'}
                        </h4>
                        <p className="text-gray-400 text-sm">
                          {verificationResult.verified 
                            ? 'Hotels and flights have been verified successfully'
                            : 'Please review the issues below'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Verification Details */}
                  <div className="space-y-3">
                    {/* Hotels Status */}
                    <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-xl">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300 text-sm">Hotels</span>
                      </div>
                      {verificationResult.hotels_verified ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <Check className="w-4 h-4" /> Verified
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-sm">
                          <X className="w-4 h-4" /> Issue Found
                        </span>
                      )}
                    </div>

                    {/* Flights Status */}
                    <div className="flex items-center justify-between p-3 bg-[#0a1628] rounded-xl">
                      <div className="flex items-center gap-2">
                        <Plane className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-300 text-sm">Flights</span>
                      </div>
                      {verificationResult.flights_verified ? (
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <Check className="w-4 h-4" /> Verified
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-red-400 text-sm">
                          <X className="w-4 h-4" /> Issue Found
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Price Change Notice */}
                  {verificationResult.price_changed && (
                    <div className={`p-4 rounded-xl ${
                      verificationResult.price_difference > 0 
                        ? 'bg-amber-500/10 border border-amber-500/30' 
                        : 'bg-green-500/10 border border-green-500/30'
                    }`}>
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${
                          verificationResult.price_difference > 0 ? 'text-amber-400' : 'text-green-400'
                        }`} />
                        <div>
                          <h4 className={`font-semibold ${
                            verificationResult.price_difference > 0 ? 'text-amber-400' : 'text-green-400'
                          }`}>
                            Price {verificationResult.price_difference > 0 ? 'Increased' : 'Decreased'}
                          </h4>
                          <p className="text-gray-400 text-sm">
                            {verificationResult.price_difference > 0 
                              ? `The package price has increased by ${formatPrice(Math.abs(verificationResult.price_difference))}`
                              : `Good news! The price has decreased by ${formatPrice(Math.abs(verificationResult.price_difference))}`}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-sm">
                        <span className="text-gray-400">Original: {formatPrice(verificationResult.original_price)}</span>
                        <span className="text-white font-semibold">New: {formatPrice(verificationResult.new_price)}</span>
                      </div>
                    </div>
                  )}

                  {/* Issues List */}
                  {verificationResult.issues && verificationResult.issues.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-red-400 font-medium text-sm">Issues:</h5>
                      {verificationResult.issues.map((issue, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{issue}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Warnings List */}
                  {verificationResult.warnings && verificationResult.warnings.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="text-amber-400 font-medium text-sm">Notes:</h5>
                      {verificationResult.warnings.map((warning, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-300">{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={closeVerificationModal}
                      className="flex-1 py-3 bg-[#0a1628] text-white border border-gray-600 hover:bg-[#0a1628]/80 rounded-xl"
                    >
                      Go Back
                    </Button>
                    {verificationResult.verified && (
                      <Button
                        onClick={proceedWithPackage}
                        className="flex-1 py-3 bg-[#d4af37] text-[#0a1628] hover:bg-[#e5c158] font-bold rounded-xl"
                        data-testid="proceed-with-package-btn"
                      >
                        <span className="flex items-center justify-center gap-2">
                          Continue <ChevronRight className="w-5 h-5" />
                        </span>
                      </Button>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackageSelection;
